/**
 * The leave-specific approval queue.
 *
 * The unified inbox at `/approvals` is the primary place to decide things. This screen
 * exists alongside it because a leave decision needs context the generic queue cannot
 * show: who else on the team is already off in the same window. Approving two of four
 * engineers onto the same week is the failure mode, and it is invisible from a list of
 * request rows.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XOctagon } from 'lucide-react';

import { api } from '@frontend/api/client';
import { usePendingLeave, type LeaveRequestView } from '@frontend/api/hooks/leave';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDateRange, humanise } from '@frontend/lib/format';
import { Button } from '@frontend/components/ui/Button';
import { DataTable, type Column } from '@frontend/components/ui/DataTable';
import { Textarea } from '@frontend/components/ui/Field';
import { Modal } from '@frontend/components/ui/Modal';
import { PageHeader } from '@frontend/components/ui/Page';
import { StatusBadge } from '@frontend/components/ui/StatusBadge';
import { EmptyState, ErrorState, LoadingRows } from '@frontend/components/ui/States';
import { useToast } from '@frontend/components/ui/Toast';

export function LeaveApprovalsPage(): JSX.Element {
  const query = usePendingLeave();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [rejecting, setRejecting] = useState<LeaveRequestView | null>(null);
  const [reason, setReason] = useState('');

  const decide = useMutation({
    mutationFn: (input: { id: string; decision: 'APPROVED' | 'REJECTED'; comment?: string }) =>
      api.patch(`/leaves/${input.id}`, {
        decision: input.decision,
        comment: input.comment ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['leave'] });
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });

  const columns: Column<LeaveRequestView>[] = [
    {
      key: 'employee',
      header: 'Employee',
      cell: (row) => (
        <Link to={`/employees/${row.employeeId}`} className="font-medium text-content-link">
          {row.employeeId}
        </Link>
      ),
    },
    { key: 'type', header: 'Type', cell: (row) => humanise(row.leaveType) },
    {
      key: 'dates',
      header: 'Dates',
      cell: (row) => formatDateRange(row.fromDate, row.toDate),
      sort: (a, b) => a.fromDate.localeCompare(b.fromDate),
    },
    { key: 'days', header: 'Days', numeric: true, cell: (row) => row.days },
    { key: 'status', header: 'Status', cell: (row) => <StatusBadge status={row.status} />, hideOnMobile: true },
    {
      key: 'reason',
      header: 'Reason',
      hideOnMobile: true,
      cell: (row) => <span className="text-content-tertiary">{row.reason ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1.5">
          <Button
            size="xs"
            variant="dangerGhost"
            leadingIcon={<XOctagon className="h-3.5 w-3.5" />}
            onClick={() => setRejecting(row)}
            data-testid={`reject-leave-${row.id}`}
          >
            Reject
          </Button>
          <Button
            size="xs"
            variant="primary"
            leadingIcon={<CheckCircle2 className="h-3.5 w-3.5" />}
            onClick={() =>
              decide.mutate(
                { id: row.id, decision: 'APPROVED' },
                {
                  onSuccess: () => toast.success('Leave approved'),
                  onError: (error) => toast.fromError(error, 'The approval was not recorded'),
                },
              )
            }
            data-testid={`approve-leave-${row.id}`}
          >
            Approve
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Leave approvals"
        description="Requests waiting on your decision. Check the team calendar before approving overlapping dates."
        actions={
          <Link to="/leave/calendar">
            <Button size="sm">Open team calendar</Button>
          </Link>
        }
      />

      {query.isLoading ? (
        <LoadingRows rows={5} columns={6} />
      ) : query.isError ? (
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : (
        <div className="surface-card">
          <DataTable
            rows={query.data?.requests ?? []}
            columns={columns}
            rowKey={(row) => row.id}
            testIdPrefix="leave-approval-row"
            emptyState={
              <EmptyState
                title="No leave waiting on you"
                description="Requests from the people you approve for appear here as soon as they are submitted, and you are notified."
              />
            }
          />
        </div>
      )}

      <Modal
        open={rejecting !== null}
        onClose={() => {
          setRejecting(null);
          setReason('');
        }}
        title="Reject this leave request"
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setRejecting(null);
                setReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="dangerGhost"
              leadingIcon={<XOctagon className="h-3.5 w-3.5" />}
              disabled={reason.trim().length < 3}
              onClick={() => {
                if (rejecting === null) return;
                decide.mutate(
                  { id: rejecting.id, decision: 'REJECTED', comment: reason.trim() },
                  {
                    onSuccess: () => toast.success('Leave rejected', 'The employee has been told why.'),
                    onError: (error) => toast.fromError(error, 'The rejection was not recorded'),
                  },
                );
                setRejecting(null);
                setReason('');
              }}
            >
              Reject
            </Button>
          </>
        }
      >
        <label className="block">
          <span className="mb-1 block text-body font-medium text-content-secondary">
            Reason <span className="text-error">*</span>
          </span>
          <Textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            autoFocus
            placeholder="The employee sees this. Say what would make the request acceptable — different dates, more notice, a different leave type."
          />
        </label>
      </Modal>
    </div>
  );
}
